import { type ReactNode, useCallback, useState } from "react";

/** Stato della sezione, non la sua categoria: il colore dice «guardami», la
 *  colonna in cui sta dice gia' di cosa parla.
 *  - `neutral`: la norma, chiusa se non serve.
 *  - `amber`: c'e' qualcosa da fare o un dato manca → si apre d'ufficio.
 *  - `red`: bloccante → sempre aperta, e non si puo' chiudere. */
export type CollapsibleTone = "neutral" | "amber" | "red";

/** L'area a cui la sezione appartiene — «di cosa parlo», non «guardami».
 *  Colora il bordo solo A RIPOSO: quando c'e' uno stato, quello vince, o
 *  un'anomalia si leggerebbe come una categoria (Davide 2026-09-03: «i
 *  contorni delle varie sezioni devono essere colorati cosi' da distinguersi
 *  meglio», ma senza perdere il colore che chiede attenzione). */
export type CollapsibleArea = "cliente" | "merce" | "soldi" | "tempo";

/** Stesse tinte della colonna in cui la sezione vive: viola chi/dove, verde
 *  acqua cosa/come, verde i soldi, blu il tempo. */
export const AREA_CLS: Record<CollapsibleArea, string> = {
  cliente: "border-l-[3px] border-l-violet",
  merce: "border-l-[3px] border-l-teal",
  soldi: "border-l-[3px] border-l-green",
  tempo: "border-l-[3px] border-l-blue",
};

const TONE_CLS: Record<CollapsibleTone, string> = {
  neutral: "border-line",
  amber: "border-amber/50 border-l-[3px] border-l-amber",
  red: "border-red/50 border-l-[3px] border-l-red",
};

/** Chiave della memoria: per SEZIONE, non per ordine. Apro «Conti per riga» e
 *  resta aperta mentre ne guardo dieci; a scheda nuova la pagina riparte nella
 *  forma canonica (sessionStorage, non local). */
function chiave(id: string): string {
  return `om.ordine.${id}`;
}

/** sessionStorage puo' lanciare — finestra privata, storage disabilitato dal
 *  browser. Una sezione che non si ricorda e' un fastidio; una pagina ordine
 *  che non si apre e' un lavoro fermo. */
function leggi(id: string): "open" | "closed" | null {
  try {
    const v = sessionStorage.getItem(chiave(id));
    return v === "open" || v === "closed" ? v : null;
  } catch {
    return null;
  }
}

function scrivi(id: string, stato: "open" | "closed"): void {
  try {
    sessionStorage.setItem(chiave(id), stato);
  } catch {
    // memoria non disponibile: la sezione funziona lo stesso, senza ricordarsi
  }
}

interface CollapsibleProps {
  /** Identifica la SEZIONE (es. "colli", "storico-cliente"): e' la chiave della memoria. */
  id: string;
  title: string;
  /** Il riassunto che si legge da CHIUSO — di solito il numero che evita di aprire. */
  summary?: ReactNode;
  tone?: CollapsibleTone;
  /** Colora il bordo a riposo. Ignorata quando `tone` non e' neutro: lo stato vince. */
  area?: CollapsibleArea;
  /** Azioni dell'intestazione: stanno FUORI dal toggle, perche' un button dentro
   *  un button e' HTML invalido e perche' devono restare raggiungibili da chiuso. */
  actions?: ReactNode;
  /** Riga di tasti in fondo al pannello, visibile anche da CHIUSO: e' la casa
   *  dei gesti della sezione (es. «Crea reso»), che prima galleggiavano fuori
   *  dal bordo, staccati da cio' su cui agiscono. */
  footer?: ReactNode;
  /** Dentro un altro pannello (es. «Dettaglio» nei Conti, «Colli e misure»
   *  nella Spedizione): un pozzetto color carta, non una seconda card — una
   *  card dentro una card raddoppia i bordi senza dire niente di più. Il tono
   *  ambra/rosso resta: lo stato si vede anche qui. */
  annidato?: boolean;
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
}

export function Collapsible({
  id,
  title,
  summary,
  tone = "neutral",
  area,
  actions,
  footer,
  annidato = false,
  defaultOpen = false,
  className = "",
  children,
}: CollapsibleProps) {
  // Apertura d'ufficio quando il tono chiede attenzione, MA una chiusura
  // esplicita vince per il resto della sessione: senza questa regola la pagina
  // riapre in faccia all'operatore, ordine dopo ordine, cio' che lui chiude
  // apposta.
  const [open, setOpen] = useState(() => {
    const ricordato = leggi(id);
    if (ricordato) return ricordato === "open";
    return defaultOpen || tone !== "neutral";
  });

  const toggle = useCallback(() => {
    setOpen((prima) => {
      const dopo = !prima;
      scrivi(id, dopo ? "open" : "closed");
      return dopo;
    });
  }, [id]);

  const bloccante = tone === "red";
  const aperto = bloccante || open;

  const intestazione = (
    <>
      {!bloccante && (
        <span
          aria-hidden="true"
          className={[
            "h-[7px] w-[7px] shrink-0 border-b-[1.8px] border-r-[1.8px] border-ink-3 transition-transform duration-150",
            aperto ? "rotate-45" : "-rotate-45",
          ].join(" ")}
        />
      )}
      <h3
        className={`whitespace-nowrap leading-tight text-ink ${annidato ? "text-[14px] font-semibold" : "text-[15px] font-bold"}`}
      >
        {title}
      </h3>
      {summary && (
        <span className="min-w-0 truncate text-[12.5px] font-normal text-ink">{summary}</span>
      )}
    </>
  );

  return (
    <section
      className={[
        annidato
          ? "overflow-hidden rounded-lg border bg-paper/70"
          : "overflow-hidden rounded-xl border bg-card shadow-[0_1px_2px_rgba(31,27,22,0.04)]",
        annidato && tone === "neutral" ? "border-transparent" : TONE_CLS[tone],
        // Solo a riposo: con un tono acceso il bordo lo porta gia' lo stato.
        tone === "neutral" && area ? AREA_CLS[area] : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="flex items-center gap-2">
        {bloccante ? (
          <div className="flex min-w-0 flex-1 items-center gap-2.5 px-4 py-3">{intestazione}</div>
        ) : (
          <button
            type="button"
            onClick={toggle}
            aria-expanded={aperto}
            className="flex min-w-0 flex-1 items-center gap-2.5 px-4 py-3 text-left transition-colors duration-150 hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50"
          >
            {intestazione}
          </button>
        )}
        {actions && <div className="flex shrink-0 items-center gap-1.5 pr-4">{actions}</div>}
      </div>
      {aperto && <div className="border-t border-line px-4 pb-3.5 pt-3">{children}</div>}
      {footer && (
        <div className="flex flex-wrap gap-2 border-t border-line bg-paper/60 px-4 py-3">
          {footer}
        </div>
      )}
    </section>
  );
}
